import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Check,
  Lock,
  Store,
  CreditCard,
  ChevronRight,
  QrCode,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime } from '../lib/mockData';
import { LanguageToggle } from '../components/LanguageToggle';
import { Logo } from '../components/Logo';
import { useTranslation } from 'react-i18next';
import { send } from '../components';
import { useLoading } from '../hooks/useLoading';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { cn } from '../service/CoreFunctions';

const PublicPayment = () => {
  const { invoiceId, ordernum } = useParams();
  const { t, i18n } = useTranslation();
  const { showLoading } = useLoading();

  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [paymentComplete, setPaymentComplete] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isInactive, setIsInactive] = useState(false);

  // Нэхэмжлэх эзэмшигч идэвхгүй/хориглогдсон үед backend-ээс ирэх кодууд
  const isInactiveError = (message) => {
    const msg = String(message || '');
    return msg.includes('RC100034') || msg.includes('RC100035');
  };

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [txnNote, setTxnNote] = useState('');

  const [invoice, setInvoice] = useState({
    id: invoiceId,
    amount: 0,
    txndesc: '',
    merchant_name: '',
    status: 'pending',
    paid_at: null,
    expire_at: null,
    count_total: 1,
    amount_total: 0,
    canceled_at: null,
    cancel_reason: null,
  });

  const createInvoice = async (payment) => {
    if (isCanceled) {
      toast.error(t('invoiceCanceled') || 'Нэхэмжлэх цуцлагдсан байна.');
      return;
    }
    if (isExpired) {
      toast.error(t('invoiceExpired') || 'Нэхэмжлэхийн хугацаа дууссан байна.');
      return;
    }
    if (isInactive) {
      toast.error(t('invoiceInactive') || 'Нэхэмжлэх идэвхгүй байна.');
      return;
    }

    if ((Number(invoice.count_total) || 1) > 1 && !txnNote.trim()) {
      toast.error(t('noteRequired') || 'Гүйлгээний утга заавал оруулна уу');
      return;
    }
    setPaymentMethod(payment)
    const res = await send("cu0903", {
      invid: invoiceId,
      paymentmethod: payment,
      description: txnNote
    }, showLoading);
    if (res.status === 'error') {
      if (isInactiveError(res.message)) {
        setIsInactive(true);
        return;
      }
      toast.error(res.message);
      return;
    }
    if (res.order && res.order.status === 'Preparing') {
      window.location.replace(res.order.negdiurl);
    } else {
      toast.error("Гүйлгээний хүсэлт амжилтгүй боллоо.");
    }
  };

  const getData = async () => {
    const res = await send("cu0902", {
      invid: invoiceId,
      ordernum: ordernum,
    }, showLoading);

    if (res.status === 'error') {
      // Эзэмшигч нь идэвхгүй бол шууд идэвхгүй дэлгэц харуулна
      if (isInactiveError(res.message)) {
        setIsInactive(true);
        setIsCanceled(false);
        setIsExpired(false);
        setPaymentComplete(false);
        setPaymentSuccess(false);
        return;
      }
      toast.error(res.message);
      return;
    }

    setInvoice(res);

    const st = (res.status ?? '').toString().toLowerCase();

    // canceled
    if (st === 'canceled') {
      setIsCanceled(true);
      setIsExpired(false);
      setPaymentComplete(false);
      // payer success дэлгэц харуулах шаардлагагүй
      setPaymentSuccess(false);
      return;
    }

    // expired (backend статус өөрөө expired болгосон байж болно)
    if (st === 'expired') {
      setIsExpired(true);
      setIsCanceled(false);
      setPaymentComplete(false);
      setPaymentSuccess(false);
      return;
    }
    // pending үед expire_at-аар front дээр давхар шалгана
    if (st === 'pending' && res?.expire_at) {
      const now = new Date();
      const expirationDate = new Date(res.expire_at);
      if (now > expirationDate) {
        setIsExpired(true);
        setIsCanceled(false);
        setPaymentComplete(false);
        setPaymentSuccess(false);
        return;
      }
    }
    // invoice бүрэн paid
    if (st === 'paid') {
      setPaymentComplete(true);
      setIsCanceled(false);
      setIsExpired(false);
      // ordernum байсан ч paid дэлгэц дээр шууд орно
      return;
    }

    // ✅ ordernum байгаа үед тухайн payer амжилттай төлсөн бол success screen гаргана
    const payerSuccess = Boolean(ordernum && res.payment_success === true);
    if (payerSuccess) {
      setPaymentSuccess(true);
    } else {
      setPaymentSuccess(false);
    }

    // бусад төлөвүүд: pending хэвээр
    setIsCanceled(false);
    setIsExpired(false);
    setPaymentComplete(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // ✅ 1) Payer төлбөр амжилттай (invoice бүрэн хаагдаагүй байж болно)
  if (paymentSuccess && !paymentComplete && !isCanceled && !isExpired) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="flex items-center justify-between px-6">
          <Logo />
          <LanguageToggle />
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center animate-fade-up">
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="h-12 w-12 text-success" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('paymentSuccessful') || 'Төлбөр амжилттай'}
            </h1>

            <p className="text-muted-foreground mb-8">
              {t('thankYouPayment')}
            </p>

            <div className="card-elevated p-6 text-left space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('amountPaid')}</span>
                <span className="text-xl font-bold">{formatCurrency(invoice.amount, i18n.language, invoice.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('merchant') || 'Мерчант'}</span>
                <span className="font-medium">{invoice.merchant_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('reference') || 'Дугаар'}</span>
                <span className="font-mono text-sm">{invoice.id}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('paid')}</span>
                <span className="font-mono text-sm">{formatDateTime(invoice.paid_at, i18n.language, invoice.currency)}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  // ✅ 2) Invoice бүрэн paid
  if (paymentComplete) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="flex items-center justify-between px-6">
          <Logo />
          <LanguageToggle />
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center animate-fade-up">
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="h-12 w-12 text-success" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('paymentSuccessful')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('thankYouPayment')}
            </p>

            <div className="card-elevated p-6 text-left space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('amountPaid')}</span>
                <span className="text-xl font-bold">{formatCurrency(invoice.amount, i18n.language, invoice.currency)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('merchant')}</span>
                <span className="font-medium">{invoice.merchant_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('reference')}</span>
                <span className="font-mono text-sm">{invoice.id}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('paid')}</span>
                <span className="font-mono text-sm">{formatDateTime(invoice.paid_at, i18n.language, invoice.currency)}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  // ✅ Нэхэмжлэх эзэмшигч идэвхгүй / хориглогдсон
  if (isInactive) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="flex items-center justify-between px-6">
          <Logo />
          <LanguageToggle />
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center animate-fade-up">
            <div className="w-24 h-24 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('invoiceInactive') || 'Нэхэмжлэх идэвхгүй'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('invoiceInactiveDesc') || 'Нэхэмжлэх үүсгэсэн хэрэглэгчийн бүртгэл идэвхгүй тул төлбөр төлөх боломжгүй.'}
            </p>

            <div className="card-elevated p-6 text-left space-y-4 opacity-60">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('reference') || 'Дугаар'}</span>
                <span className="font-mono text-sm">{invoiceId}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  // ✅ 3) Canceled
  if (isCanceled) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="flex items-center justify-between px-6">
          <Logo />
          <LanguageToggle />
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center animate-fade-up">
            <div className="w-24 h-24 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('invoiceCanceled') || 'Нэхэмжлэх цуцлагдсан'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('invoiceCanceledDesc') || 'Энэ нэхэмжлэхийг мерчант цуцалсан тул төлбөр хийх боломжгүй.'}
            </p>

            <div className="card-elevated p-6 text-left space-y-4 opacity-60">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('amount')}</span>
                <span className="text-xl font-bold">
                  {formatCurrency(invoice.amount, i18n.language, invoice.currency)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('merchant')}</span>
                <span className="font-medium">{invoice.merchant_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('reference')}</span>
                <span className="font-mono text-sm">{invoice.id}</span>
              </div>

              {/* Хэрвээ backend өгдөг бол */}
              {invoice.canceled_at && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">{t('canceledAt') || 'Цуцалсан'}</span>
                  <span className="font-mono text-sm">
                    {formatDateTime(invoice.canceled_at, i18n.language)}
                  </span>
                </div>
              )}

              {invoice.cancel_reason && (
                <div className="pt-2 text-xs text-muted-foreground">
                  {t('reason') || 'Шалтгаан'}: {invoice.cancel_reason}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }
  // ✅ 4) Expired
  if (isExpired) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="flex items-center justify-between px-6">
          <Logo />
          <LanguageToggle />
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center animate-fade-up">
            <div className="w-24 h-24 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <AlertCircle className="h-12 w-12 text-warning" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('invoiceExpired')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('invoiceExpiredDesc')}
            </p>

            <div className="card-elevated p-6 text-left space-y-4 opacity-60">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('amount')}</span>
                <span className="text-xl font-bold">{formatCurrency(invoice.amount, i18n.language, invoice.currency)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('merchant')}</span>
                <span className="font-medium">{invoice.merchant_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('reference')}</span>
                <span className="font-mono text-sm">{invoice.invid}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('expires')}</span>
                <span className="font-mono text-sm text-red-500">{formatDateTime(invoice.expire_at, i18n.language)}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  // ✅ 5) Default: Payment page
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between px-6 border-b border-border h-16">
        <Logo />
        <LanguageToggle />
      </header>

      <main className="flex-1 p-6">
        <div className="max-w-lg mx-auto space-y-6 animate-fade-up">
          {/* Invoice Summary */}
          <div className="card-elevated p-6 space-y-4">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Store className="h-4 w-4" />
              {invoice.merchant_name}
            </div>

            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-1">{t('amount')}</p>
              <p className="text-4xl font-bold text-foreground">
                {formatCurrency(invoice.amount, i18n.language, invoice.currency)}
              </p>
            </div>

            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <p className="text-sm text-muted-foreground">{invoice.txndesc}</p>
            </div>
          </div>

          {/* Customer Note for Split Payments */}
          {(Number(invoice.count_total) || 1) > 1 && (
            <div className="card-elevated p-6 space-y-3">
              <div className="flex justify-between items-center">
                <Label htmlFor="txnNote" className="text-base font-semibold">
                  {t('paymentNote')} <span className="text-destructive">*</span>
                </Label>
                <span className={cn(
                  "text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors",
                  txnNote.length >= 60
                    ? "text-destructive border-destructive/20 bg-destructive/5"
                    : "text-muted-foreground border-border/50 bg-muted/30"
                )}>
                  {txnNote.length} / 60
                </span>
              </div>
              <Input
                id="txnNote"
                required
                value={txnNote}
                onChange={(e) => setTxnNote(e.target.value)}
                placeholder={t('enterNote')}
                className="bg-background"
                maxLength={60}
              />
            </div>
          )}

          <div className="space-y-3">
            <h2 className="text-lg font-semibold">{t('choosePaymentMethod')}</h2>

            {/* New Card */}
            <button
              onClick={() => {
                createInvoice('card');
                // setSelectedSavedCard(null);
              }}
              className={`card-interactive w-full p-4 text-left ${paymentMethod === 'card' ? 'ring-2 ring-primary' : ''
                }`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{t('payWithCard')}</p>
                  <p className="text-sm text-muted-foreground">{t('supportedCards')}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </button>

            <button
              onClick={() => createInvoice('qr')}
              className={`card-interactive w-full p-4 text-left ${paymentMethod === 'qr' ? 'ring-2 ring-primary' : ''
                }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                  <QrCode className="h-6 w-6 text-secondary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{t('payWithQR')}</p>
                  <p className="text-sm text-muted-foreground">{t('scanWithMobileApp')}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </button>
          </div>

          {/* <Button
            type="submit"
            variant="gradient"
            size="xl"
            className="w-full"
            disabled={loading}
            onClick={createInvoice}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('processing')}
              </>
            ) : (
              <>
                <ShieldCheck className="mr-2 h-5 w-5" />
                {t('payNow')} {formatCurrency(invoice.amount, i18n.language)}
              </>
            )}
          </Button> */}

          <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3" />
            {t('securedBySSL')}
          </p>
        </div>
      </main>
    </div>
  );
};

export default PublicPayment;
